import { useState, useMemo, FormEvent, useEffect } from "react";
import { Box, Checkbox, TextField, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Toolbar, TableSortLabel, IconButton } from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import { alpha } from '@mui/material/styles';
// import SearchIcon from '@mui/icons-material/Search';

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

interface TableDisplayComponentProps {
  columns: Array<any>;
  members: Array<any>;
  selectedMembers: Array<string>;
  tableTitle: string;
  handleAction: any;
  actionIcon: any;
  disabledMembers?: Array<string>;
  rowsPerPage: number;
  page: number;
  enableAction?: boolean;
}
const TableDisplayComponent = (props: TableDisplayComponentProps) => {
  const [selectedMembers, setSelectedMembers] = useState<Array<string>>(props.selectedMembers);
  // For Group Members Table
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('id');
  // const [searchField, setSearchField] = useState<string>("");
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);

  function EnhancedTableHead(props1: EnhancedTableProps) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
      props1;
    const createSortHandler =
      (property: string) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
      };

    useEffect(() => {
      setSelectAllChecked(rowCount > 0 && numSelected === (rowCount - (props.disabledMembers || []).length));
    }, [numSelected])

    return (
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < (rowCount - (props.disabledMembers || []).length)}
              checked={selectAllChecked}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'Select all',
              }}
            />
          </TableCell>
          {props.columns.map((column) => (
            <TableCell
              key={column.id}
              align={column.numeric ? 'right' : 'left'}
              padding={column.disablePadding ? 'none' : 'normal'}
              sortDirection={orderBy === column.id ? order : false}
            >
              <TableSortLabel
                active={orderBy === column.id}
                direction={orderBy === column.id ? order : 'asc'}
                onClick={createSortHandler(column.id)}
              >
                {column.label}
                {orderBy === column.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
  interface EnhancedTableToolbarProps {
    numSelected: number;
  }
  function EnhancedTableToolbar(props2: EnhancedTableToolbarProps) {
    // window.setTimeout(() => {
    //   if (document.getElementById("search-field"))
    //     // @ts-ignore
    //     document.getElementById("search-field").focus();
    // }, 100);
    const { numSelected } = props2;
    return (
      <Toolbar
        sx={[
          {
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          },
          numSelected > 0 && {
            bgcolor: (theme) =>
              alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
          },
        ]}
      >
        {numSelected > 0 ? (
          <Typography
            sx={{ flex: '1 1 100%' }}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {numSelected} selected
          </Typography>
        ) : (
          <Typography
            sx={{ flex: '1 1 100%' }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            {props.tableTitle}
          </Typography>
        )}
        {(numSelected > 0) && (props.enableAction) ? (
          <div onClick={() => { props.handleAction(selectedMembers) }}>
            {props.actionIcon}
          </div>
        ) : (
          <></>
          // <TextField
          //   id="search-field"
          //   label="Search"
          //   value={searchField}
          //   onChange={(e) => { setSearchField(e.target.value) }}
          // />
          //   <IconButton type="button" sx={{ p: '10px' }} aria-label="search" onClick={() => { }}>
          //     <SearchIcon />
          //   </IconButton>

        )}
      </Toolbar>
    );
  }

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = () => {
    if (!selectAllChecked) {
      const newSelected = props.members.reduce((acc: any, el: any) =>
        (props.disabledMembers || []).includes(el.id) ? acc : acc = [...acc, el.id], []);
      setSelectedMembers(newSelected);
      setSelectAllChecked(true);
      return;
    }
    setSelectedMembers([]);
    setSelectAllChecked(false);
  };

  const handleMembersSelection = (selected: boolean, id: string) => {
    if (selected) {
      let _res = selectedMembers.filter((el: string) => el != id);
      setSelectedMembers(_res);
    } else {
      let _set = Object.assign([], selectedMembers);
      _set.push(id);
      setSelectedMembers(_set);
    }
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    props.page > 0 ? Math.max(0, (1 + props.page) * props.rowsPerPage - props.members.length) : 0;

  const visibleRows = useMemo(
    () => {
      return [...props.members].sort(getComparator(order, orderBy))
    },
    [order, orderBy, props.members],
  );

  // const searchResult = (el: any) => {
  //   return searchField != "" ? props.columns.some((e) => el[e['id']].toLowerCase().includes(searchField.toLowerCase())) : true;
  // }

  return (
    <>
      <EnhancedTableToolbar numSelected={selectedMembers.length} />
      <TableContainer>
        <Table
          aria-labelledby="tableTitle"
          size={'medium'}
        >
          <EnhancedTableHead
            numSelected={selectedMembers.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={props.members.length}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              const isItemSelected = selectedMembers.includes(row.id) || (props.disabledMembers || []).includes(row.id);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <TableRow
                  hover
                  // @ts-ignore
                  onClick={(event: FormEvent<HTMLTableRowElement>) => handleMembersSelection(isItemSelected, row.id)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  selected={isItemSelected}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      disabled={(props.disabledMembers || []).includes(row.id)}
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                    />
                  </TableCell>
                  {props.columns.map((column: any, ind: number) => {
                    return <TableCell
                      component="td"
                      key={ind}
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row[column['id']]}
                    </TableCell>
                  })}
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (0) * emptyRows,
                }}
              >
                <TableCell colSpan={props.columns.length + 1} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default TableDisplayComponent